// Copyright (c) 2020 WiseTime. All rights reserved.

import { intersection } from "ramda"
import { SiteRef } from "generated/wisetime/type/site_ref_pb"
import {
  DIALOG_WRAPPER_CLASSNAME,
  DROPDOWN_CONTENT_CLASSNAME,
  RESTRICTED_OVERLAY_CLASSNAME,
} from "../features/shared/classNames"

// Use same fallback array reference to avoid unnecessary rerender.
// e.g. for useSelector() hook https://react-redux.js.org/api/hooks#equality-comparisons-and-updates
export const emptyArray: never[] = []

export type Option = {
  value: string | number,
  label: string,
  secondaryLabel?: string,
  tertiaryLabel?: string,
}

export type NonEmptyArray<T> = T[] & { 0: T }

export const notUndefined = <T>(value: T | undefined): value is T => value !== undefined

// See https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String/localeCompare
export const naturalOrderCollator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" })

export const sortTeams = (teams: SiteRef[]): SiteRef[] => (
  [...teams].sort((team1, team2) => (
    team1.displayName.toLowerCase() > team2.displayName.toLowerCase() ? 1 : -1
  ))
)

// See https://stash.practiceinsight.io/stash/projects/WT/repos/wise-time/browse/wisetime-console/src/main/java/io/wisetime/console/client/application/mytime/AbbreviateUtil.java
export const abbreviate = (name: string): string => {
  let abbr = ""
  const words = name.toUpperCase().replace("&", "").split(" ")
  const [firstWord, secondWord] = words
  if (!firstWord) {
    return abbr
  }
  abbr += firstWord.charAt(0)
  if (!secondWord) {
    abbr += firstWord.length > 1 ? firstWord.charAt(1) : ""
    return abbr
  }
  abbr += secondWord.charAt(0)
  return abbr
}

export const validateAll = (...results: boolean[]): boolean => {
  return results.reduce((prev, result) => result && prev, true)
}

export const reorderArray = <T>(
  list: T[],
  sourceIndex: number,
  destinationIndex: number,
): T[] => {
  const result = Array.from(list)
  const [removed] = result.splice(sourceIndex, 1)
  if (removed) {
    result.splice(destinationIndex, 0, removed)
  }
  return result
}

export const presentInAllLists = <T extends string | number>(listsToCheck: T[][]): T[] => {
  if (listsToCheck.length === 0) {
    return []
  }
  return listsToCheck.reduce((result: T[], list: T[]): T[] => (
    intersection(result, list)
  ))
}

// Be careful it can produce a false-positive result when there're different types with the same properties
// to distinguish them you will need additional check e.g.
// by unique identifier via `some` method against an array with the specific type or use another approach
// https://rangle.io/blog/how-to-use-typescript-type-guards/
export const isOfType = <T>(
  varToBeChecked: unknown,
  propertyToCheckFor: keyof T,
): varToBeChecked is T => (varToBeChecked as T)[propertyToCheckFor] !== undefined

export const isString = (value: unknown): value is string => typeof value === "string" || value instanceof String

export const isStaff = (email: string): boolean => (
  email.endsWith("@practiceinsight.io")
  || email.endsWith("@wisetime.com")
  || email.endsWith("@staff.wisetime.com")
  || email.endsWith("@humaan.com.au")
  || email === "mf@fleuchaus.com"
  || email === "stanislavc848@gmail.com"
)

export const extractSearchWords = (str: string): string[] => (
  str.split(/\s+/).reduce(
    (result: string[], word: string): string[] => {
      const wordLowerCase = word.toLowerCase()
      if (wordLowerCase && !result.includes(wordLowerCase)) {
        return result.concat(wordLowerCase)
      }
      return result
    },
    [],
  )
)

export const isDisabledShortcut = (): boolean => {
  const tagName: string | undefined = document.activeElement?.tagName?.toLowerCase()
  const selectors = `.${DIALOG_WRAPPER_CLASSNAME}`
   + `, .${DROPDOWN_CONTENT_CLASSNAME}`
   + `, .${RESTRICTED_OVERLAY_CLASSNAME}`
  return document.querySelector(selectors) !== null
    || tagName === "input" || tagName === "textarea"
}

export const isTouchDevice = "ontouchstart" in window

export const isOldSafari = (): boolean => {
  const isSafari = /apple/i.test(navigator.vendor)
  const majorVersion = isSafari ?
    navigator.userAgent.split(" ").find(item => item.startsWith("Version/"))
      ?.split(/[/.]/)[1] : undefined
  const sonomaSafariVersion = 17
  return Number(majorVersion) < sonomaSafariVersion
}

export const lineBreak = "\n"
